import type * as React from "react";
import { type FC, type ReactNode, useContext, useMemo, useState } from "react";
import classNames from "classnames";
import { Button, Stack, TextField } from "~/common/mui";
import { IconClickable } from "~/common/components/icon/IconClickable";
import { IconType } from "~/common/components/icon/IconType";
import Checkbox from "~/common/components/Checkbox";
import ProductImage from "~/domains/product/components/ProductImage";
import ProductDesignation from "~/features/productDetails/components/ProductDesignation";
import { GET_LINEAR_CONFIGURATOR_URL_FOR_ITEM } from "~/features/configurator/Linear/constants";
import { type ProductData } from "~/domains/product/types/ProductData";
import { DefaultConfirmationContext } from "~/features/configurator/Linear/components/ConfirmationContext";
import {
  SavedConfigContext,
  type SavedConfigurationItem,
} from "~/features/savedConfigurations/SavedConfigurationsView";
import { CommentButton } from "~/common/components/CommentButton/CommentButton";
import { Row } from "~/common/components/Layout";
import LanguageAwareExternalContent from "~/features/configurator/Linear/components/LanguageAwareExternalContent";
import { userIsCustomer } from "~/domains/user/utils/roles";
import { Card } from "~/features/configurator/Linear/components/Card";
import { canSeeSalesFinder } from "~/features/navigation/components/headerMenu/DesktopPersonalNav";
import { CART_PRODUCT_IMAGE_WIDTHS } from "~/domains/cart/utils/cartConstants";
import { ImageFormat, ImageType } from "~/domains/product/types/ProductDataDTO";
import { resolveUrl } from "~/utils/url/urlResolver";
import ShareConfigurationsMenu from "~/features/savedConfigurations/components/ShareConfigurationsMenu";
import ShareConfigurationsViaEmailPopup from "~/features/savedConfigurations/components/ShareConfigurationsViaEmailPopup";
import { useTranslation } from "react-i18next";

import classes from "~/features/savedConfigurations/components/SavedConfigurationsTableEntry.module.less";

import { CommentInput, useShowAndScrollInput } from "~/common/components/CommentInput/CommentInput";
import { useForcedConfiguratorTranslations } from "~/features/configurator/common/useXmlConfiguratorTranslations";
import { ConfiguratorType, getConfiguratorTypeFromProductCode } from "~/domains/configurator/types/ConfiguratorType";
import { UNIT_CONFIGURATOR_PRODUCT_CODE } from "~/domains/configurator/constants";

interface Props {
  index: number;
  item: SavedConfigurationItem;
}

const IS_ALT_EXTERNAL_CONTENT = /^[a-zA-Z0-9_-]+$/;

const SavedConfigurationsTableEntry: FC<Props> = ({ index, item }) => {
  const { t } = useTranslation();

  const [name, setName] = useState(item.name);
  const [comment, setComment] = useState(item.comment);
  const contextData = useContext(SavedConfigContext);

  const code = item.code;

  const isEdited = item.name !== name;
  const saveItem = () => contextData.updateItem(code, name, comment);
  const deleteItem = () => contextData.deleteItem(code);
  const selectItem = (newVal: boolean) => contextData.selectItem(code, newVal);

  const updateComment = (newComment: string) => {
    contextData.updateItem(code, name, newComment);
  };

  const updateShowComment = (newValue: boolean) => {
    contextData.toggleComment(code, newValue);
  };

  const shareConfigurationsViaSalesPartnerFinder = (items: SavedConfigurationItem[]) => {
    contextData.shareItemsViaSalesPartnerFinder(items);
  };

  const configuratorType = getConfiguratorTypeFromProductCode(item.schaefflerId) ?? ConfiguratorType.MetricShaft;

  const url = GET_LINEAR_CONFIGURATOR_URL_FOR_ITEM(item.code, item.importUrlPrefix);

  const isCustomer = userIsCustomer();

  const [shareConfigurationsMenuAnchorEl, setShareConfigurationsMenuAnchorEl] = useState<Element | null>(null);
  const [isShareConfigurationsMenuOpen, setIsShareConfigurationsMenuOpen] = useState(false);
  const [isShareConfigurationsViaEmailPopupOpen, setIsShareConfigurationsViaEmailPopupOpen] = useState(false);

  // TODO - Missing configurator type
  const xmlT = useForcedConfiguratorTranslations(item.schaefflerId).t;
  const isOldConfigurator = item.schaefflerId === UNIT_CONFIGURATOR_PRODUCT_CODE;

  const product: Pick<
    ProductData,
    "images" | "manufacturerLogoUrl" | "brandLogoUrl" | "url" | "isOptime" | "sapMaterialNumber" | "code"
  > = useMemo(() => {
    return {
      images: [
        {
          imageType: ImageType.primary,
          url: item.useXmlTranslation && xmlT ? xmlT(item.mediaUrl) : item.mediaUrl,
          altText: "",
          format: ImageFormat.thumbnail,
        },
      ],
      manufacturerLogoUrl: item.manufacturerUrl || undefined,
      brandLogoUrl: undefined,
      url,
      isOptime: false,
      sapMaterialNumber: "",
      code: item.schaefflerId,
    };
  }, [item.manufacturerUrl, item.mediaUrl, url]);

  const [commentRef, toggleComment] = useShowAndScrollInput(() => updateShowComment(!item.showComment));

  const shortDescription = item.shortDescription ?? "";
  let descriptionProps;
  if (shortDescription.includes("http")) {
    descriptionProps = {
      content: shortDescription,
    };
  } else if (IS_ALT_EXTERNAL_CONTENT.test(shortDescription)) {
    const url = `https://cdn.schaeffler-ecommerce.com/frontend/linear/${shortDescription}_en-GB.html`;
    descriptionProps = {
      content: url,
    };
  } else {
    descriptionProps = {
      forceXmlConfiguratorCode: isOldConfigurator ? undefined : item.schaefflerId,
      content: shortDescription,
    };
  }

  return (
    <div className={"scha__lcsc__table__entry"}>
      <div className={"scha__lcsc__table__left-side scha__lcsc__table__left-side__col-container"}>
        <div>{index}</div>

        <div className={"d-flex mx-3"}>
          <div className={classes.imageContainer}>
            <ProductImage className={"mb-3 mr-3"} product={product} widths={CART_PRODUCT_IMAGE_WIDTHS} />
          </div>

          <Stack gap={1.25}>
            <a href={resolveUrl(url)}>
              <ProductDesignation
                size={"title-medium"}
                designation={item.designation}
                isPreferred={false}
                tooltipSide={"bottom"}
              />
            </a>

            <p className="mt-1 mb-0">
              <LanguageAwareExternalContent {...descriptionProps} className={"scha__lc__external-label"} />
            </p>
          </Stack>
        </div>
      </div>

      <div className={"scha__lcsc__table__right-side"}>
        <Card className={"py-3"} innerClassName={"scha__lcsc__table__right-side__col-container"} disableHover={true}>
          <div className={classes.cardInner}>
            <div className={classes.firstRow}>
              <div className={classes.cardLeftSide}>
                <Cell>
                  <div className={"d-flex flex-align-baseline w-100"}>
                    <TextField
                      fullWidth
                      label={t("page.savedLinearConfigurations.table.header.name")}
                      onChange={(e) => setName(e.target.value)}
                      value={name}
                    />
                    {isEdited && (
                      <div className={"ml-3"}>
                        <Button size={"medium"} color={"secondary"} onClick={saveItem} disabled={item.isBeingUpdated}>
                          {t("generic.save")}
                        </Button>
                      </div>
                    )}
                  </div>
                </Cell>
              </div>
              <div className={classes.icons}>
                <Cell justifyEnd={true}>
                  <Stack gap={2}>
                    {canSeeSalesFinder() && !isCustomer && (
                      <>
                        <IconClickable
                          className={"scha_wishlist_deleteitem"}
                          type={IconType.Share}
                          disabled={item.isBeingDeleted}
                          onClick={(e) => {
                            setShareConfigurationsMenuAnchorEl(e.currentTarget);
                            setIsShareConfigurationsMenuOpen(true);
                          }}
                        />
                        <ShareConfigurationsMenu
                          anchorEl={shareConfigurationsMenuAnchorEl}
                          isOpen={isShareConfigurationsMenuOpen}
                          onClose={() => setIsShareConfigurationsMenuOpen(false)}
                          shareConfigurationsViaEmail={() => {
                            setIsShareConfigurationsMenuOpen(false);
                            setIsShareConfigurationsViaEmailPopupOpen(true);
                          }}
                          shareConfigurationsViaSalesPartnerFinder={() => {
                            setIsShareConfigurationsMenuOpen(false);
                            shareConfigurationsViaSalesPartnerFinder([item]);
                          }}
                        />
                        {isShareConfigurationsViaEmailPopupOpen && (
                          <ShareConfigurationsViaEmailPopup
                            onClose={() => setIsShareConfigurationsViaEmailPopupOpen(false)}
                            items={[item]}
                            onSuccess={() => setIsShareConfigurationsViaEmailPopupOpen(false)}
                          />
                        )}
                      </>
                    )}
                    {isCustomer && (
                      <>
                        <IconClickable
                          className={"scha_wishlist_deleteitem"}
                          type={IconType.Share}
                          disabled={item.isBeingDeleted}
                          onClick={(e) => {
                            setIsShareConfigurationsViaEmailPopupOpen(true);
                          }}
                        />
                        {isShareConfigurationsViaEmailPopupOpen && (
                          <ShareConfigurationsViaEmailPopup
                            onClose={() => setIsShareConfigurationsViaEmailPopupOpen(false)}
                            items={[item]}
                            onSuccess={() => setIsShareConfigurationsViaEmailPopupOpen(false)}
                          />
                        )}
                      </>
                    )}
                    <CommentButton
                      onClick={toggleComment}
                      open={item.showComment}
                      fontSize={"small"}
                      className={"scha__saved-configurations__comment-button"}
                    />
                    <DefaultConfirmationContext onConfirm={deleteItem}>
                      {(openConfirmation) => (
                        <IconClickable
                          onClick={openConfirmation}
                          className={"scha_wishlist_deleteitem"}
                          type={IconType.Bin}
                          disabled={item.isBeingDeleted}
                        />
                      )}
                    </DefaultConfirmationContext>

                    <Checkbox
                      checked={item.isSelected}
                      className={"scha_wishlist_checkbox"}
                      id={"wishlist_small_icon_checkbox"}
                      onCheck={selectItem}
                    />
                  </Stack>
                </Cell>
              </div>
            </div>
            <div className={classes.commentMargin}>
              <Row className={"mt-3"}>
                <div className={classes.comment}>
                  {item.showComment && (
                    <CommentInput
                      value={item.comment}
                      onChange={updateComment}
                      ref={commentRef}
                      label={t("wishlistPage.comment.label")}
                    />
                  )}
                </div>
              </Row>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

const Cell: React.FC<{ justifyEnd?: boolean; children?: ReactNode }> = ({ children, justifyEnd }) => {
  const cls = classNames("px-3 d-flex flex-align-start", justifyEnd && "flex-justify-end");
  return <div className={cls}>{children}</div>;
};

export default SavedConfigurationsTableEntry;
